import { assign } from 'fsm';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import Raven from 'raven-js';
import { getObjectType, getObjectTypeLabel, isContactObjectType, wait } from '../../utils';
import { enrichRecords } from '../../BulkEnrichmentModal/useBulkEnrichment/client';
import { ErrorAlertMessage } from '../../ErrorAlertMessage';
import { usageTracker } from '../../../usageTracker';
import { fetchPropertyDefinitions, performDataTest } from './client';
import { transformDataTestProperties } from './transforms';
import { jsx as _jsx } from "react/jsx-runtime";
export const machine = {
  initial: 'initializing',
  states: {
    initializing: {
      always: [{
        target: 'testing',
        actions: ['setEnrichmentType', 'setRecordsSelected', 'trackOpenPanel']
      }]
    },
    closing: {
      invoke: {
        src: 'wait',
        onDone: {
          target: 'done',
          actions: ['closeModal']
        }
      }
    },
    idle: {
      on: {
        ENRICH: {
          target: 'enriching',
          actions: ['callEnrichmentStart']
        },
        TEST: 'testing',
        CLOSE: 'closing',
        SET_OVERWRITE: {
          target: 'idle',
          actions: ['setOverwrite']
        }
      }
    },
    testing: {
      invoke: {
        src: 'performDataTest',
        onDone: {
          target: 'loadingDefinitions',
          actions: ['setPropertyMatchRates']
        },
        onError: {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }
      },
      on: {
        ENRICH: 'enriching',
        CLOSE: 'closing'
      },
      tags: ['loading']
    },
    loadingDefinitions: {
      invoke: {
        src: 'fetchPropertyDefinitions',
        onDone: {
          target: 'idle',
          actions: ['setPropertyDefinitions', 'setProperties']
        },
        onError: {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }
      },
      on: {
        CLOSE: 'closing'
      }
    },
    enriching: {
      invoke: {
        src: 'enrichRecords',
        onDone: {
          target: 'enrichedRecords',
          actions: ['logEnrichment', 'addEnrichSuccess', 'trackEnrichedRecords']
        },
        onError: {
          target: 'errorEnrichment',
          actions: ['setErrorId', 'addEnrichError', 'logError']
        }
      },
      tags: ['loading']
    },
    enrichedRecords: {
      invoke: {
        src: 'wait',
        onDone: {
          target: 'closing'
        }
      },
      on: {
        CLOSE: 'closing'
      }
    },
    done: {},
    error: {
      on: {
        TEST: 'testing',
        CLOSE: 'closing',
        RETRY: 'testing',
        SET_OVERWRITE: {
          target: 'idle',
          actions: ['setOverwrite']
        }
      },
      tags: ['error']
    },
    errorEnrichment: {
      on: {
        ENRICH: 'enriching',
        CLOSE: 'closing',
        RETRY: 'enriching'
      },
      tags: ['error']
    },
    errorUsage: {
      on: {
        RETRY: 'loadingCredits',
        CLOSE: 'closing'
      },
      tags: ['error']
    }
  },
  actions: {
    addEnrichError: context => {
      const {
        addAlertDanger,
        companyLabelPluralLowercase,
        contactLabelPluralLowercase,
        errorId,
        objectTypeId
      } = context;
      const objectTypeLabel = isContactObjectType(objectTypeId) ? contactLabelPluralLowercase : companyLabelPluralLowercase;
      addAlertDanger === null || addAlertDanger === void 0 || addAlertDanger({
        message: /*#__PURE__*/_jsx(ErrorAlertMessage, {
          errorId: errorId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "enrichmentProperties.modals.enrichRecords.alerts.error.description",
            options: {
              objectType: objectTypeLabel
            }
          })
        }),
        titleText: I18n.text('enrichmentProperties.modals.enrichRecords.alerts.error.title', {
          objectType: objectTypeLabel
        })
      });
    },
    addEnrichSuccess: context => {
      const {
        addAlertSuccess,
        companyLabelLowercase,
        companyLabelPluralLowercase,
        contactLabelLowercase,
        contactLabelPluralLowercase,
        objectTypeId,
        recordsSelected
      } = context;
      const labels = {
        companyLabelLowercase,
        companyLabelPluralLowercase,
        contactLabelLowercase,
        contactLabelPluralLowercase
      };
      const objectTypeLabel = getObjectTypeLabel(objectTypeId, recordsSelected, labels);
      addAlertSuccess === null || addAlertSuccess === void 0 || addAlertSuccess({
        message: I18n.text('enrichmentProperties.modals.enrichRecords.alerts.success.description', {
          count: recordsSelected,
          objectType: objectTypeLabel
        }),
        titleText: I18n.text('enrichmentProperties.modals.enrichRecords.alerts.success.title')
      });
    },
    callEnrichmentStart: context => {
      var _context$onEnrichment;
      (_context$onEnrichment = context.onEnrichmentStart) === null || _context$onEnrichment === void 0 || _context$onEnrichment.call(context);
    },
    closeModal: context => {
      var _context$onClose;
      (_context$onClose = context.onClose) === null || _context$onClose === void 0 || _context$onClose.call(context);
    },
    logEnrichment: context => {
      var _context$onLogging;
      (_context$onLogging = context.onLogging) === null || _context$onLogging === void 0 || _context$onLogging.call(context, {
        selectionIds: context.selectionIds
      });
    },
    logError: (_context, event) => {
      const {
        responseJSON
      } = event.data;
      Raven.captureException(responseJSON);
    },
    setRecordsSelected: assign(context => {
      const {
        selectionIds
      } = context;
      const recordsSelected = selectionIds.length;
      return {
        recordsSelected
      };
    }),
    setErrorId: assign((_context, event) => {
      const {
        responseJSON
      } = event.data;
      const {
        correlationId
      } = responseJSON;
      return {
        errorId: correlationId
      };
    }),
    setPropertyDefinitions: assign((_context, event) => {
      return {
        propertyDefinitions: event.data
      };
    }),
    setProperties: assign(context => {
      return {
        properties: transformDataTestProperties({
          propertyDefinitions: context.propertyDefinitions,
          propertyMatchRates: context.propertyMatchRates
        })
      };
    }),
    setOverwrite: assign((_context, event) => {
      return {
        shouldOverwrite: event.data.shouldOverwrite
      };
    }),
    setPropertyMatchRates: assign((_context, event) => {
      return Object.assign({}, event.data);
    }),
    setEnrichmentType: assign(context => {
      if (context.objectTypeId === CONTACT_TYPE_ID) {
        return {
          enrichmentType: 'contact'
        };
      }
      return {
        enrichmentType: 'company'
      };
    }),
    trackEnrichedRecords: context => {
      usageTracker.track('enriched-record', {
        objectType: getObjectType(context.objectTypeId),
        enrichmentType: 'dataTest',
        selectionType: 'all',
        propertyCount: 0,
        selectionCount: 0,
        selectionDifference: 0
      });
    },
    trackOpenPanel: context => {
      usageTracker.track('interaction', {
        screen: 'enrichment-data-test',
        subscreen: getObjectType(context.objectTypeId),
        action: 'opened-enrichment-data-test-panel'
      });
    }
  },
  guards: {},
  services: {
    enrichRecords,
    fetchPropertyDefinitions,
    performDataTest,
    wait: async () => {
      await wait(500);
    }
  }
};